import React from 'react';
import FredChart from './FredChart';
import RatePrediction from './RatePrediction';
import { formatValue, trendLabel } from '../format';
import { getPeriod } from '../config';

/**
 * Shell shared by the seven factor tabs: heading, the current/change/trend
 * tiles, the FRED chart, and the model outlook.
 *
 * Each factor tab composes this and adds whatever is specific to it via
 * `children`, so a tab can be redesigned without touching the other six.
 */
const FactorTabLayout = ({ tab, data, period, children }) => {
  const { marketData, getComparison, getPrediction, error } = data;

  const currentValue = marketData?.data?.[tab.dataKey];
  const comparison = getComparison(currentValue, tab.dataKey);
  const prediction = getPrediction(tab.dataKey);
  const periodLabel = getPeriod(period).label;

  const Unavailable = () => <span className="text-base font-normal text-gray-400">Not available</span>;

  return (
    <div className="mb-8">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center space-x-4 mb-4">
          <span className="text-4xl">{tab.icon}</span>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{tab.label}</h2>
            <p className="text-gray-600">{tab.description}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="text-sm text-blue-600 font-medium">Current Value</div>
            <div className="text-2xl font-bold text-blue-900">
              {formatValue(currentValue, tab.valueType)}
            </div>
          </div>

          <div className="bg-green-50 rounded-lg p-4">
            <div className="text-sm text-green-600 font-medium">Change vs {periodLabel} ago</div>
            <div className="text-xl font-bold text-green-900">
              {comparison && !comparison.status ? (
                <>
                  {comparison.difference > 0 ? '+' : ''}
                  {comparison.difference.toFixed(2)}
                </>
              ) : (
                <Unavailable />
              )}
            </div>
          </div>

          <div className="bg-amber-50 rounded-lg p-4">
            <div className="text-sm text-amber-600 font-medium">Trend</div>
            <div className="text-xl font-bold text-amber-900">
              {comparison && !comparison.status ? trendLabel(comparison.trend) : <Unavailable />}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">📈 Live Market Chart</h3>
          <FredChart series={tab.fredSeries} title={tab.fredTitle} />
          <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-800">
              <strong>Chart source:</strong> Federal Reserve Economic Data (FRED), showing the last ten
              years through today. Use the link below the chart for the interactive version.
            </p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">🎯 Outlook</h3>
          <RatePrediction
            prediction={prediction}
            unavailableReason={error.predictions}
            valueType={tab.valueType}
          />
        </div>
      </div>

      {children}
    </div>
  );
};

export default FactorTabLayout;
